import styled from 'styled-components';

// This page is always client-branded, so its colours come straight from the white label API
// (theme.brand, mapped from header_background / background in injectTheme). The CAMS page
// shell is not reused for the shell itself: it swaps in a dark default whenever the hostname
// is a CAMS default host -- localhost included -- which would hide the brand colours here.
const brandValue = (theme, key, fallback) => theme?.brand?.[key] || fallback;

// Body copy follows the white label's text colour. Hints and secondary lines use the same
// colour at lower emphasis rather than a second hardcoded grey.
const textColor = theme => brandValue(theme, 'text', '#495c6d');

export const PageShell = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  ${({ theme }) => {
    const background = brandValue(theme, 'background', '#0D256D');
    const image = theme?.brand?.backgroundImage;

    return image
      ? `background: url(${image}), ${background};
         background-size: cover;
         background-position: center;`
      : `background: ${background};`;
  }}
`;

export const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  padding: 15px 30px;
  position: relative;
  z-index: 10;
  color: ${({ theme }) => brandValue(theme, 'headerText', '#fff')};
  ${({ theme }) => {
    const background = brandValue(theme, 'headerBackground', '#0D256D');
    const image = theme?.brand?.headerBackgroundImage;

    return image
      ? `background: url(${image}), ${background};
         background-size: cover;
         background-position: center;`
      : `background: ${background};`;
  }}
`;

// Additions specific to the RBC onboarding flow. The page shell, inputs, buttons and footer
// come from the CAMS transition pages (../CamsLogin/styles) so the two standalone branded
// pages stay visually consistent.

// The brand logo is served from white-label config and may be an SVG, a PNG or a JPG.
// SVGs are the awkward case: one with only a viewBox has no intrinsic pixel size, and one
// with neither viewBox nor dimensions falls back to the browser's 300x150 default. Pinning
// the height and letting the width follow gives every format a predictable size.
export const LogoWrap = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
`;

export const HeaderLogo = styled.img`
  display: block;
  height: 40px;
  width: auto;
  max-width: 220px;
  /* Keeps the aspect ratio if max-width clamps a wide wordmark. */
  object-fit: contain;
  object-position: left center;

  @media (max-width: 480px) {
    height: 32px;
    max-width: 150px;
  }
`;

// The CAMS equivalents of these run every colour through a helper that discards the brand
// value on CAMS default hosts (localhost included), so they are redefined here reading
// theme.brand straight from the white label API.
export const Title = styled.h1`
  margin: 0 0 15px;
  font-family: 'Muli', sans-serif;
  font-weight: 600;
  font-size: 29px;
  line-height: 1.2;
  color: ${({ theme }) => brandValue(theme, 'title', '#331850')};
  text-align: center;
`;

export const Subtitle = styled.p`
  margin: 0 0 25px;
  padding-bottom: 15px;
  font-weight: 400;
  font-size: 0.95em;
  line-height: 1.5;
  color: ${({ theme }) => brandValue(theme, 'text', '#495c6d')};
  text-align: center;
`;

export const Spinner = styled.svg`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  animation: rbc-button-spin 1s linear infinite;

  @keyframes rbc-button-spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

export const Button = styled.button`
  margin-top: 5px;
  padding: 12px 32px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  border-radius: 4px;
  background-color: ${({ theme }) => brandValue(theme, 'primary', '#5CB85C')};
  color: ${({ theme }) => brandValue(theme, 'primaryText', '#fff')};
  font-size: 1em;
  font-weight: 600;
  text-align: center;
  cursor: pointer;
  transition: opacity 0.2s ease;
  /* Also rendered as an anchor (as="a") where the action is navigation. An anchor lets its
     text be selected and shows a tap highlight, neither of which a <button> does -- so a
     drag or tap on the label looked like a stray text selection. */
  box-sizing: border-box;
  text-decoration: none;
  white-space: nowrap;
  line-height: 1.2;
  user-select: none;
  -webkit-tap-highlight-color: transparent;

  /* nowrap keeps a label on one line, so give the longest labels room on a narrow screen
     rather than letting them overflow the card. */
  @media (max-width: 400px) {
    padding: 12px 16px;
    font-size: 0.95em;
  }

  &:hover,
  &:focus {
    opacity: 0.8;
    color: ${({ theme }) => brandValue(theme, 'primaryText', '#fff')};
    text-decoration: none;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// The CAMS text link hardcodes #000, which is the one colour on this page that would not
// follow the white label.
export const TextLink = styled.a`
  display: block;
  margin-top: 20px;
  text-align: center;
  font-size: 0.9em;
  color: ${({ theme }) => brandValue(theme, 'primary', '#4341AA')};
  text-decoration: none;
  transition: opacity 0.2s ease;

  &:hover,
  &:focus {
    opacity: 0.8;
    text-decoration: underline;
    color: ${({ theme }) => brandValue(theme, 'primary', '#4341AA')};
  }
`;

export const PageFooter = styled.footer`
  margin-top: 20px;
  width: 100%;
  /* Matches the card above it so the footer links line up with its edges. */
  max-width: 600px;
  text-align: center;
  position: relative;
  z-index: 5;
  color: ${({ theme }) => brandValue(theme, 'footerText', '#e9eff4')};
`;

export const FooterLinks = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 12px;

  a,
  span {
    color: ${({ theme }) => brandValue(theme, 'footerText', '#e9eff4')};
  }

  a {
    text-decoration: none;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.8;
      text-decoration: underline;
    }
  }
`;

export const FooterCopyright = styled.div`
  font-size: 12px;
  color: ${({ theme }) => brandValue(theme, 'footerText', '#e9eff4')};
`;

// Local copy of the CAMS card so this page can be wider without widening /cams.
export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 30px 60px 50px;
  background-color: #fff;
  color: ${({ theme }) => textColor(theme)};
  border-radius: 3px;
  max-width: 600px;
  width: 100%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

  @media (max-width: 576px) {
    padding: 30px 30px 40px;
    margin: 0 15px;
  }
`;

export const MainContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 20px 40px;
  position: relative;
  z-index: 5;
`;

export const StepCounter = styled.p`
  margin: 0 0 8px;
  font-size: 12px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => textColor(theme)};
  opacity: 0.75;
`;

export const StepBar = styled.div`
  display: flex;
  gap: 6px;
  margin: 0 0 24px;
`;

export const StepSegment = styled.span`
  flex: 1;
  height: 4px;
  border-radius: 2px;
  background: ${({ theme, done }) =>
    done ? theme?.brand?.primary || '#4341AA' : 'rgba(111, 128, 139, 0.25)'};
`;

// The migration notice on the intro step. This page is standalone, so it cannot use the
// shared Alert that the portal shell renders for the e6 and call-centre banners -- but it
// takes that component's `warn` palette from basicTheme verbatim, so the two read as one
// system. The colours are deliberately not brand-driven: a notice has to stay legible
// whatever a white label sets its primary and background to.
export const NoticeBanner = styled.div`
  margin: 0 0 24px;
  padding: 12px 15px;
  border: 1px solid #faebcc;
  border-radius: 2px;
  background-color: #fcf8e3;
  color: #8a6d3b;
  font-size: 14px;
  line-height: 1.5;
  text-align: left;

  strong {
    font-weight: 600;
  }
`;

export const Section = styled.section`
  margin-bottom: 24px;
  text-align: left;

  h3 {
    margin: 0 0 6px;
    font-size: 15px;
    font-weight: 600;
  }

  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
    color: ${({ theme }) => textColor(theme)};
  }
`;

export const ChecklistItem = styled.li`
  position: relative;
  padding-left: 22px;
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => textColor(theme)};
  list-style: none;

  &:before {
    content: '';
    position: absolute;
    left: 4px;
    top: 8px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ theme }) => theme?.brand?.primary || '#4341AA'};
  }
`;

export const Checklist = styled.ul`
  margin: 0;
  padding: 0;
`;

export const Note = styled.p`
  margin: 0 0 24px;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => textColor(theme)};
  opacity: 0.75;
  text-align: left;
`;

export const CardSummary = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  margin-bottom: 20px;
  border: 1px solid rgba(111, 128, 139, 0.25);
  border-radius: 8px;
  font-size: 14px;
  text-align: left;

  strong {
    font-weight: 600;
  }

  span {
    font-size: 13px;
    color: ${({ theme }) => textColor(theme)};
    opacity: 0.75;
  }
`;

export const AgreementRow = styled.div`
  margin-bottom: 12px;
  text-align: left;
  font-size: 13px;
  line-height: 1.5;
`;

export const CaptchaWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 4px 0 16px;
`;

export const FieldHint = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  color: ${({ theme }) => textColor(theme)};
  opacity: 0.75;
  text-align: left;
`;

// Masked field with a show/hide toggle. The toggle itself is the shared PwdEyeButton used by
// the register and activate pages, so the icon and behaviour match.
export const PasswordFieldWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  input {
    padding-right: 44px;
  }

  button {
    position: absolute;
    right: 8px;
    display: flex;
    align-items: center;
    padding: 4px;
    border: 0;
    background: none;
    cursor: pointer;
  }
`;

export const RuleList = styled.ul`
  margin: 8px 0 0;
  padding: 0;
  list-style: none;
  text-align: left;
`;

export const Rule = styled.li`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  line-height: 1.6;
  color: ${({ theme, met }) => (met ? '#1c7a4a' : textColor(theme))};
  opacity: ${({ met }) => (met ? 1 : 0.75)};

  &:before {
    content: '${({ met }) => (met ? '✓' : '•')}';
    width: 12px;
    text-align: center;
  }
`;

export const SecondaryButton = styled.button`
  width: 100%;
  margin-top: 12px;
  padding: 12px 16px;
  border: 1px solid rgba(111, 128, 139, 0.4);
  border-radius: 8px;
  background: transparent;
  color: ${({ theme }) => textColor(theme)};
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background: rgba(111, 128, 139, 0.08);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
